import React, { useState } from 'react';
import { StatusBadge } from '../status/StatusBadge';
import { KTSVG } from '../../utils';
import { MainTableRow } from './MainTableRow';
import { useTranslation } from 'react-i18next';
import { IOrderHateoasLinks, IOrderItem } from './OrderDetails';
import './OrderItem.scss';
import { TableComponent } from '../TableComponent';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { useOrdersContext } from '../../store/order/order-context';
import { setRefundItems } from '../../store/order/actions/order';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { RefundModalContent } from './RefundModalContent';
import { numberFormatter } from '../../utils/utilities';

const COLUMNS = [
	{
		key: 'qty',
		title: 'ORDERS.QTY'
	},
	{
		key: 'item',
		title: 'ORDERS.ITEM'
	},
	{
		key: 'reference',
		title: 'ORDERS.REFERENCE'
	},
	{
		key: 'unitPrice',
		title: 'ORDERS.UNIT_PRICE'
	},
	{
		key: 'discount',
		title: 'ORDERS.DISCOUNT'
	},
	{
		key: 'tax',
		title: 'ORDERS.TAX'
	},
	{
		key: 'amount',
		title: 'ORDERS.ITEMS_TABLE_AMOUNT'
	}
];

export interface IOrderItemsParams {
	amountToRefund: number,
	refundMessage: string,
}

export enum REFUND_TYPE {
	REFUND_AMOUNT = 'refundAmount',
	REFUND_ITEMS = 'refundItems'
}

interface IOrderItemProps {
	orderId?: string;
	pageName: string;
	actionLinks: IOrderHateoasLinks[];
}

interface IRefundModalStateProps {
	isOpen: boolean,
	refundType: REFUND_TYPE | null,
}

export const OrderItem = (props: IOrderItemProps): JSX.Element => {
	const { orderId, pageName, actionLinks } = props;

	const refundAction = actionLinks?.find(link => link?.actionName?.toLowerCase() === 'refund' && link?.category?.toLowerCase() === 'orders');

	const { t } = useTranslation();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const { orderContextState: { orderDetailsData, isRefundItemsLoading }, dispatch } = useOrdersContext();
	const { orderItems, currencySymbol, amount } = orderDetailsData;
	const [reason, setReason] = useState<string>('');
	const [checkedItems, setCheckedItems] = useState<IOrderItem[]>([]);
	const [amountValue, setAmountValue] = useState<number>(0);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [refundModalState, setRefundModalState] = useState<IRefundModalStateProps>({
		isOpen: false,
		refundType: null
	});

	// const activeItem = orderTableRowData?.activityItems?.find(item => item.type.toLowerCase() === 'authorization');

	const onHeaderClick = (value: boolean): void => {
		const checkedItems: IOrderItem[] = [];
		if (value) {
			orderItems?.forEach((el: IOrderItem) => {
				checkedItems.push(el);
			});
		}
		setCheckedItems(checkedItems);
	};

	const refundClaims = checkRoles(authUser, CLAIMS.SUPER_ADMIN)
		|| checkRoles(authUser, CLAIMS.PARTNER_ADMIN)
		|| checkRoles(authUser, CLAIMS.MERCHANT_ADMIN)
		|| checkRoles(authUser, CLAIMS.MERCHANT_SUPPORT_ADMIN);

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));
	const closeRefundModal = (): void => setRefundModalState(prev => ({ ...prev, isOpen: false, refundType: null }));

	const errorModal = (responseCode?: string, responseMessage?: string): void => {
		setModalState({
			isOpen: true,
			modalTitle: 'Error',
			content: (
				<div className="text-center mt-10 fs-6">
					{
						responseCode && responseMessage
							? `${responseCode} ${responseMessage}`
							: 'Refund failed'
					}
				</div>
			),
			onConfirm: closeModal,
			confirmButtonText: 'Ok',
			contentClassName: 'd-block',
			minHeight: '75px',
		});
	};

	const onInputChange = (amount): void => {
		let value = Number(amount);
		if (value < 0) {
			value *= -1;
			setAmountValue(value);
		} else {
			setAmountValue(value);
		}
	};

	const mainCheckbox = ():JSX.Element | null => (
		refundClaims
			? (
				<th className='w-25px'>
					<div className='form-check form-check-sm form-check-custom form-check-solid'>
						<input
							className='form-check-input'
							type='checkbox'
							checked={Boolean(checkedItems.length) && checkedItems.length === orderItems?.length}
							onChange={(e): void => onHeaderClick(e.target.checked)}
						/>
					</div>
				</th>
			)
			: null

	);

	const amountToRefund = (): number => {
		let total = 0;
		checkedItems?.forEach(item => {
			total += item.amount;
		});
		return total;
	};

	const handleRefund = async (refundType: REFUND_TYPE): Promise<void> => {
		const params: IOrderItemsParams = {
			// transactionId: activeItem?.paymentTransactionId,
			// isPartialRefund: Boolean(amountValue),
			amountToRefund: Number(amountValue),
			refundMessage: reason
		};

		switch (refundType) {
			case REFUND_TYPE.REFUND_AMOUNT:
				params.amountToRefund = Number(amountValue);
				break;
			case REFUND_TYPE.REFUND_ITEMS:
				params.amountToRefund = amountToRefund();
				break;
		}

		await setRefundItems({
			dispatch, orderId,
			params, errorModal,
			closeModal: closeRefundModal,
			refundAction
		});
		setCheckedItems([]);
	};

	return (
		<>
			<div className="card" data-cy="order-items-block">
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-center">
						<div>
		          <span className="fs-4 fw-bolder me-5">
		            {t('ORDERS.ITEMS')}
		          </span>
							<StatusBadge
								status="cancelled"
								badgeContent={orderItems?.length}
								additionalClasses="fs-8 pt-2 pb-2 ps-3 pe-3"
							/>
						</div>
						<div>
		          <span className="fs-8 fw-bold text-muted me-5">
		            {t('ORDERS.CURRENCY')}
		          </span>
							<StatusBadge
								status="regular"
								badgeContent={currencySymbol}
								additionalClasses="fs-8 text-dark pt-2 pb-2 ps-3 pe-3"
							/>
						</div>
					</div>
				</div>
				<TableComponent
					additionalColumnStart={mainCheckbox()}
					column={COLUMNS}
					tableData={orderItems}
					tableMinHeight={orderItems?.length ? '' : 'table-min-height'}
					getRowComponent={(itemProps, index): JSX.Element => (
						<MainTableRow
							{...itemProps}
							key={`${index}-${itemProps.referenceId}`}
							setCheckedItems={setCheckedItems}
							checkedItems={checkedItems}
							pageName={pageName}
							currencySymbol={currencySymbol}
						/>
					)}
				/>
				<div className="card-body">
					<div className="separator"/>
					<div className="d-flex align-items-center justify-content-end fs-6 fw-bolder my-5">
						<div className="me-5">Order total</div>
						<div data-cy="order-total">
							{currencySymbol || ''}{numberFormatter(amount ? amount : 0)}
						</div>
					</div>
					<div className="separator" />

				</div>
				{
					refundClaims && (
					<div className="card-header pb-5 border-bottom-0 flex-column-reverse flex-sm-row">
						<div className="d-flex justify-content-between align-items-start p-0 mt-10 mt-sm-0 col-sm-8">
							<div className="col-5">
								<ButtonComponent
									buttonType={BUTTON_TYPE.WHITE}
									additionalClass="d-flex justify-content-between align-items-center action-buttons no-letter-spacing fs-6 py-1 ps-2 pe-3 w-100"
									isDisabled={!checkedItems.length || !refundAction}
									onButtonClick={(): void => setRefundModalState({ isOpen: true, refundType: REFUND_TYPE.REFUND_ITEMS })}
								>
									<>
										<KTSVG path="/media/icons/refund-button.svg" svgClassName="refund-icon"/>
										{t('ORDERS.BUTTON_NAME_ONE_ITEM')}
									</>
								</ButtonComponent>
								<div className="text-muted fs-8 text-center  button-text">
									{t('ORDERS.BUTTON_TITLE_ONE_ITEM')}
								</div>
							</div>

							<div className="buttons-divider" />

							<div className="col-5">
									<ButtonComponent
										buttonType={BUTTON_TYPE.WHITE}
										additionalClass="d-flex justify-content-between align-items-center action-buttons no-letter-spacing fs-6 py-1 ps-2 pe-3 w-100"
										isDisabled={!refundAction || !!checkedItems.length}
										onButtonClick={(): void => setRefundModalState({ isOpen: true, refundType: REFUND_TYPE.REFUND_AMOUNT })}
									>
										<>
											<KTSVG path="/media/icons/refund-all-button.svg" svgClassName="refund-icon"/>
											{t('ORDERS.BUTTON_NAME_ALL_ITEMS')}
										</>
									</ButtonComponent>
									<div className="text-muted fs-8 text-center button-text">
										{t('ORDERS.BUTTON_TITLE_ALL_ITEMS')}
									</div>
								</div>
						</div>
						<div className="col-sm-4">
							<input
								placeholder={t('ORDERS.ENTER_AMOUNT')}
								type="number"
								className="col-5 col-sm-10 amount-input ps-5 pe-2 d-block ms-auto"
								value={amountValue ? amountValue : undefined}
								onChange={(e): void => onInputChange(e.target.value)}
								disabled={!refundAction || !!checkedItems.length}
							/>
						</div>
					</div>
				)}
				{
					!refundClaims && (
						<div className="card-body">
							<div className="text-center fs-6 text-muted">Refund functionality available only for Merchant Admin</div>
						</div>
					)
				}

			</div>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={refundModalState.isOpen}
				modalTitle={refundModalState?.refundType === REFUND_TYPE.REFUND_AMOUNT ? 'Void / Refund Amount' : 'Void / Refund line(s)'}
				contentClassName='d-block'
				minHeight={295}
				onClose={closeRefundModal}
				content={
					<RefundModalContent
						closeModal={closeRefundModal}
						refundType={refundModalState.refundType}
						onConfirm={handleRefund}
						isRefundItemsLoading={isRefundItemsLoading}
						setReason={setReason}
					/>
				}
			/>
		</>
	);
};
